import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  useAddProjectLayoutMutation,
  useUpdateLayoutCalibrationMutation,
} from "../../projects/apiSlice";
import { CalibrationPoint, Coordinate, RealWorldCoordinate } from "../types";
import { CoordinateTransformer } from "../coordinateTransformer";
import { PDFProcessor } from "../pdfProcessor";
import SiteLayoutCanvas from "./SiteLayoutCanvas";
import CalibrationModal from "./CalibrationModal";

interface AddLayoutWizardModalProps {
  show: boolean;
  projectId: number;
  onHide: () => void;
  onLayoutCreated: () => void;
}

async function dataUrlToBlob(dataUrl: string): Promise<Blob> {
  const res = await fetch(dataUrl);
  return res.blob();
}

const AddLayoutWizardModal: React.FC<AddLayoutWizardModalProps> = ({
  show,
  projectId,
  onHide,
  onLayoutCreated,
}) => {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [imageUrl, setImageUrl] = useState("");
  const [uploadBlob, setUploadBlob] = useState<Blob | null>(null);
  const [uploadName, setUploadName] = useState("layout.png");
  const [layoutName, setLayoutName] = useState("");
  const [isProcessingPdf, setIsProcessingPdf] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [calibrationPoints, setCalibrationPoints] = useState<
    CalibrationPoint[]
  >([]);
  const [pendingPoint, setPendingPoint] = useState<Coordinate | null>(null);

  const [addLayout, { isLoading: isCreating }] = useAddProjectLayoutMutation();
  const [updateCalibration, { isLoading: isSaving }] =
    useUpdateLayoutCalibrationMutation();

  const isCalibrated = calibrationPoints.length >= 2;
  const busy = isCreating || isSaving;

  const transformer = useMemo(() => {
    if (calibrationPoints.length < 2) return null;
    try {
      return new CoordinateTransformer(calibrationPoints);
    } catch {
      return null;
    }
  }, [calibrationPoints]);

  useEffect(() => {
    if (!show) return;
    setStep(1);
    setImageUrl("");
    setUploadBlob(null);
    setUploadName("layout.png");
    setLayoutName("");
    setCalibrationPoints([]);
    setPendingPoint(null);
    setIsProcessingPdf(false);
    setError(null);
  }, [show]);

  const handleFile = useCallback(async (file: File) => {
    setError(null);
    const isPdf =
      file.type === "application/pdf" ||
      file.name.toLowerCase().endsWith(".pdf");

    if (isPdf) {
      setIsProcessingPdf(true);
      try {
        const processor = new PDFProcessor();
        const { imageDataUrl } = await processor.processPDF(file);
        setImageUrl(imageDataUrl);
        setUploadBlob(await dataUrlToBlob(imageDataUrl));
        setUploadName(file.name.replace(/\.pdf$/i, ".png"));
        setCalibrationPoints([]);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Unable to process PDF file");
      } finally {
        setIsProcessingPdf(false);
      }
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setImageUrl((e.target?.result as string) || "");
      setUploadBlob(file);
      setUploadName(file.name);
      setCalibrationPoints([]);
    };
    reader.onerror = () => setError("Unable to read the selected file");
    reader.readAsDataURL(file);
  }, []);

  const handleCanvasClick = useCallback(
    (coord: Coordinate) => {
      if (calibrationPoints.length >= 2) return;
      setPendingPoint(coord);
    },
    [calibrationPoints.length],
  );

  const handleCalibrationSave = useCallback(
    (rw: RealWorldCoordinate) => {
      if (!pendingPoint) return;
      setCalibrationPoints((prev) => [
        ...prev,
        { imageCoordinate: pendingPoint, realWorldCoordinate: rw },
      ]);
      setPendingPoint(null);
    },
    [pendingPoint],
  );

  const handleSave = useCallback(async () => {
    if (!uploadBlob || !isCalibrated) {
      setError("Upload a layout and add 2 calibration points first.");
      return;
    }
    setError(null);
    try {
      const formData = new FormData();
      formData.append("imageFile", uploadBlob, uploadName);
      if (layoutName.trim()) formData.append("name", layoutName.trim());

      const created = await addLayout({ projectId, formData }).unwrap();
      const layoutId = created.id;

      await updateCalibration({
        layoutId,
        body: {
          calibrationPoints: calibrationPoints.map((p) => ({
            layout_id: layoutId,
            image_x: p.imageCoordinate.x,
            image_y: p.imageCoordinate.y,
            real_northing: p.realWorldCoordinate.northing,
            real_easting: p.realWorldCoordinate.easting,
          })),
        },
      }).unwrap();

      onLayoutCreated();
      onHide();
    } catch (e: any) {
      setError(e?.data?.message || "Failed to save layout. Please try again.");
    }
  }, [
    uploadBlob,
    uploadName,
    layoutName,
    isCalibrated,
    addLayout,
    projectId,
    updateCalibration,
    calibrationPoints,
    onLayoutCreated,
    onHide,
  ]);

  const stepTitle =
    step === 1
      ? "Step 1 of 3 — Upload Layout"
      : step === 2
        ? "Step 2 of 3 — Calibrate Coordinates"
        : "Step 3 of 3 — Confirm & Save";

  return (
    <div className={`modal-backdrop ${show ? "show" : ""}`}>
      <div className="modal" style={{ maxWidth: 760 }}>
        <div className="modal-head">
          <h3>Add Layout · {stepTitle}</h3>
          <button type="button" className="btn btn-sm" onClick={onHide}>
            <i className="bi bi-x-lg" />
          </button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="badge alarm" style={{ marginBottom: 12 }}>
              {error}
            </div>
          )}

          {step === 1 && (
            <div className="grid" style={{ gap: 12 }}>
              <div className="field">
                <label>Layout name (optional)</label>
                <input
                  className="input"
                  value={layoutName}
                  onChange={(e) => setLayoutName(e.target.value)}
                  placeholder="e.g. Basement Level 1"
                />
              </div>
              <div className="field">
                <label>Layout image or PDF</label>
                <input
                  className="input"
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleFile(f);
                  }}
                />
              </div>
              {isProcessingPdf && (
                <div className="text-muted">Processing PDF…</div>
              )}
              {imageUrl && (
                <img
                  src={imageUrl}
                  alt="Preview"
                  style={{
                    maxWidth: "100%",
                    maxHeight: 280,
                    objectFit: "contain",
                    border: "1px solid var(--c-border)",
                    borderRadius: 6,
                  }}
                />
              )}
            </div>
          )}

          {step === 2 && (
            <div>
              <p className="text-muted" style={{ marginTop: 0 }}>
                Click two points on the layout and enter their real-world
                coordinates. {calibrationPoints.length}/2 set.
              </p>
              <SiteLayoutCanvas
                imageUrl={imageUrl}
                calibrationPoints={calibrationPoints}
                devices={[]}
                isCalibrated={isCalibrated}
                transformer={transformer}
                isAddingMode={!isCalibrated}
                onImageClick={handleCanvasClick}
                height={420}
              />
              {calibrationPoints.length > 0 && (
                <button
                  type="button"
                  className="btn btn-sm"
                  style={{ marginTop: 8 }}
                  onClick={() => setCalibrationPoints([])}
                >
                  <i className="bi bi-arrow-counterclockwise" /> Reset points
                </button>
              )}
            </div>
          )}

          {step === 3 && (
            <div>
              <p className="text-muted" style={{ marginTop: 0 }}>
                Review and save. The layout will be uploaded with{" "}
                {calibrationPoints.length} calibration points.
              </p>
              <SiteLayoutCanvas
                imageUrl={imageUrl}
                calibrationPoints={calibrationPoints}
                devices={[]}
                isCalibrated={isCalibrated}
                transformer={transformer}
                height={420}
              />
            </div>
          )}
        </div>

        <div className="modal-foot">
          {step > 1 && (
            <button
              type="button"
              className="btn"
              onClick={() => setStep((s) => (s - 1) as 1 | 2 | 3)}
              disabled={busy}
            >
              Back
            </button>
          )}
          {step < 3 && (
            <button
              type="button"
              className="btn btn-primary"
              disabled={
                (step === 1 && !imageUrl) || (step === 2 && !isCalibrated)
              }
              onClick={() => setStep((s) => (s + 1) as 1 | 2 | 3)}
            >
              Next
            </button>
          )}
          {step === 3 && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSave}
              disabled={busy || !isCalibrated}
            >
              {busy ? "Saving…" : "Save Layout"}
            </button>
          )}
        </div>
      </div>

      <CalibrationModal
        show={pendingPoint !== null}
        stepNumber={calibrationPoints.length + 1}
        onCancel={() => setPendingPoint(null)}
        onSave={handleCalibrationSave}
      />
    </div>
  );
};

export default AddLayoutWizardModal;
